import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import { ReportService } from '../../services/reportService';
import { CalibrationReportData, CategoryCalibration } from '../../types/CalibrationReport';
import { ReportExportButton } from './ReportExportButton';

export const CalibrationReport: React.FC = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState<CalibrationReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default to last 30 days
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ReportService.getCalibrationReport(startDate, endDate);
      setReport(data);
    } catch {
      setError('Failed to load calibration report.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const getDeviationColor = (deviation: number): 'success' | 'warning' | 'error' => {
    const abs = Math.abs(deviation);
    if (abs <= 0.3) return 'success';
    if (abs <= 0.8) return 'warning';
    return 'error';
  };

  const getDeviationLabel = (cat: CategoryCalibration): string => {
    if (Math.abs(cat.deviation) <= 0.3) return '✅ Good agreement';
    if (cat.deviation > 0) return '⚠️ AI underscores';
    return '⚠️ AI overscores';
  };

  // Rendered above every branch — loading, error and empty included. A period that fails to load
  // or returns nothing is exactly when a user needs to change the period, and returning a bare
  // Alert leaves them with no control but to leave the tab and come back.
  const dateFilter = (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button variant="contained" size="small" onClick={fetchReport}>
          Update
        </Button>
        <ReportExportButton
          report="calibration"
          label="AI Calibration"
          startDate={startDate}
          endDate={endDate}
          busy={loading}
        />
        <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
          Dates are calendar days in the agency&apos;s timezone.
        </Typography>
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {dateFilter}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress aria-label="Loading calibration report" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {dateFilter}
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!report || report.totalReviewedCalls === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {dateFilter}
        <Alert severity="info">
          No calls with supervisor score overrides found between {startDate} and {endDate}.
          Calibration data appears once supervisors edit individual criterion scores on the call detail page.
          An export of this period will contain the report headings and no data rows.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {dateFilter}

      {/* Summary Cards */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Calls with Overrides</Typography>
            <Typography variant="h4">{report.callsWithOverrides}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Avg AI Score</Typography>
            <Typography variant="h4">{report.averageAiScore}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Avg Supervisor Score</Typography>
            <Typography variant="h4">{report.averageSupervisorScore}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Agreement Rate (±5 pts)</Typography>
            <Typography variant="h4">{report.agreementRate}%</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Score Comparison Bars */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Score Comparison</Typography>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ width: 120 }}>AI Score</Typography>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                variant="determinate"
                value={report.averageAiScore}
                sx={{ height: 20, borderRadius: 1 }}
              />
            </Box>
            <Typography variant="body2" sx={{ width: 50, textAlign: 'right' }}>
              {report.averageAiScore}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ width: 120 }}>Supervisor</Typography>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                variant="determinate"
                value={report.averageSupervisorScore}
                color="secondary"
                sx={{ height: 20, borderRadius: 1 }}
              />
            </Box>
            <Typography variant="body2" sx={{ width: 50, textAlign: 'right' }}>
              {report.averageSupervisorScore}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Average Deviation: {report.averageDeviation > 0 ? '+' : ''}{report.averageDeviation} points
          {report.averageDeviation < 0 ? ' (AI scores higher)' : report.averageDeviation > 0 ? ' (Supervisors score higher)' : ''}
        </Typography>
      </Paper>

      {/* Deviation Histogram */}
      {report.deviationHistogram.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Deviation Distribution</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {report.deviationHistogram.map((bucket) => {
              const maxCount = Math.max(...report.deviationHistogram.map(b => b.count));
              return (
                <Box key={bucket.range} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ width: 100, textAlign: 'right', fontFamily: 'monospace' }}>
                    {bucket.range}
                  </Typography>
                  <Box sx={{ flexGrow: 1, maxWidth: 300 }}>
                    <Box
                      sx={{
                        height: 20,
                        width: `${(bucket.count / maxCount) * 100}%`,
                        bgcolor: bucket.rangeMin >= 0 ? 'info.main' : 'warning.main',
                        borderRadius: 0.5,
                        minWidth: 4,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {bucket.count} call{bucket.count !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Category-Level Calibration */}
      {report.categoryCalibrations.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Category-Level Calibration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Where does AI disagree with supervisors most?
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">AI Avg</TableCell>
                  <TableCell align="right">Supervisor Avg</TableCell>
                  <TableCell align="right">Deviation</TableCell>
                  <TableCell align="right">Override Rate</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.categoryCalibrations.map((cat) => (
                  <TableRow key={cat.categoryName}>
                    <TableCell>{cat.categoryName}</TableCell>
                    <TableCell align="right">{cat.aiAverage.toFixed(1)}</TableCell>
                    <TableCell align="right">{cat.supervisorAverage.toFixed(1)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={getDeviationLabel(cat)}>
                        <span>{cat.deviation > 0 ? '+' : ''}{cat.deviation.toFixed(1)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{cat.overrideRate}%</TableCell>
                    <TableCell>
                      <Chip
                        label={getDeviationLabel(cat)}
                        color={getDeviationColor(cat.deviation)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Recent Overridden Calls */}
      {report.recentOverriddenCalls.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Recent Overridden Calls</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Incident #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">AI Score</TableCell>
                  <TableCell align="right">Supervisor Score</TableCell>
                  <TableCell align="right">Delta</TableCell>
                  <TableCell>Reviewer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.recentOverriddenCalls.map((call) => (
                  <TableRow
                    key={call.callId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/call/${call.callId}`)}
                  >
                    <TableCell>{call.incidentNumber}</TableCell>
                    <TableCell>{new Date(call.callDate).toLocaleDateString()}</TableCell>
                    <TableCell align="right">{call.aiScore}</TableCell>
                    <TableCell align="right">{call.supervisorScore}</TableCell>
                    <TableCell align="right" sx={{
                      color: call.delta > 0 ? 'success.main' : call.delta < 0 ? 'error.main' : 'text.primary'
                    }}>
                      {call.delta > 0 ? '+' : ''}{call.delta}
                    </TableCell>
                    <TableCell>{call.reviewedBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};
